/**
 * The searchable dropdown the campaign module uses for its Template field:
 * a 38px bordered trigger, a popover the width of that trigger, a search box
 * pinned above a scrolling list, and rows of "name … right-hand slot … tick".
 *
 * 🔴 EXTRACTED FROM TemplatePicker RATHER THAN COPIED. That component keeps its
 * name and its API — it now renders this and supplies the category pill as the
 * right-hand slot — so there is exactly ONE set of these styles. A second copy
 * is the thing that drifts the first time the trigger height or the popover
 * radius is adjusted in one place and not the other.
 *
 * The markup here is moved verbatim, not redesigned: the campaign Template
 * field must look the same after this extraction as before it.
 */
import { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Plus, Search } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface PickerSelectOption {
  id: string;
  name: string;
  /** Right-hand slot, before the tick. The category pill, a count, anything. */
  meta?: React.ReactNode;
  /** Matched by the search box alongside `name`. */
  keywords?: string[];
}

export function PickerSelect({
  options,
  selectedId,
  onSelect,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyMessage = "No results found.",
  disabled = false,
  triggerTestId,
  searchTestId,
  createLabel,
  onCreate,
  createTestId,
}: {
  options: PickerSelectOption[];
  selectedId: string;
  onSelect: (id: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  triggerTestId?: string;
  searchTestId?: string;
  /** Pinned under the list, e.g. "Create new campaign". Hidden without it. */
  createLabel?: string;
  onCreate?: () => void;
  createTestId?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // A stale query would silently hide rows the next time it opens.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      [o.name, ...(o.keywords || [])].some((s) => String(s || "").toLowerCase().includes(q)),
    );
  }, [query, options]);

  const selected = options.find((o) => o.id === selectedId) ?? null;

  return (
    <Popover open={disabled ? false : open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={triggerTestId}
          disabled={disabled}
          className="w-full flex items-center justify-between rounded-lg bg-white outline-none focus:border-[#0EB64F] transition-colors disabled:cursor-not-allowed disabled:opacity-60"
          style={{ border: '1px solid #EDF1F7', height: 38, padding: '0 11px', fontFamily: 'var(--wn-font)' }}
        >
          <span
            className="truncate"
            style={{ fontSize: 13, fontWeight: 500, color: selected ? '#101828' : '#6A7282' }}
          >
            {selected ? selected.name : placeholder}
          </span>
          <ChevronDown className="w-4 h-4 text-[#98A2B3] flex-shrink-0 ml-2" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        className="p-0 rounded-xl border border-[#EDF1F7] shadow-lg bg-white"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <div className="px-3 py-2.5 border-b border-[#EDF1F7]">
          <div className="flex items-center gap-2 h-8 px-2.5 rounded-lg bg-[#FBFCFD] border border-[#EDF1F7]">
            <Search className="w-3.5 h-3.5 text-[#99A1AF] flex-shrink-0" />
            <input
              autoFocus
              data-testid={searchTestId}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className="flex-1 min-w-0 bg-transparent border-none outline-none text-[13px] text-[#101828] placeholder:text-[#99A1AF]"
              style={{ fontFamily: 'var(--wn-font)' }}
            />
          </div>
        </div>
        <div className="max-h-[260px] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <p className="px-4 py-4 text-center text-[13px]" style={{ fontFamily: 'var(--wn-font)', color: '#6A7282' }}>
              {emptyMessage}
            </p>
          ) : (
            filtered.map((o) => {
              const isCurrent = o.id === selectedId;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => { onSelect(o.id); setOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[#F4F6FA]"
                  style={{ background: isCurrent ? '#F4F6FA' : undefined }}
                >
                  <span
                    className="flex-1 min-w-0 truncate leading-snug text-[13px] font-medium"
                    style={{ fontFamily: 'var(--wn-font)', color: '#364153' }}
                  >
                    {o.name}
                  </span>
                  {o.meta}
                  {isCurrent && <Check className="w-3.5 h-3.5 text-[#0EB64F] flex-shrink-0" strokeWidth={2.5} />}
                </button>
              );
            })
          )}
        </div>
        {createLabel && onCreate ? (
          // Pinned below the list rather than inside it: a row that scrolls
          // with the options reads as one of them, and an empty list would
          // hide the only way out of it.
          <div className="border-t border-[#EDF1F7] p-1">
            <button
              type="button"
              data-testid={createTestId}
              onClick={() => {
                setOpen(false);
                onCreate();
              }}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-[#F4F6FA]"
            >
              <Plus className="h-3.5 w-3.5 flex-shrink-0 text-[#0EB64F]" strokeWidth={2.5} />
              <span
                className="truncate text-[13px] font-semibold leading-snug text-[#0EB64F]"
                style={{ fontFamily: "var(--wn-font)" }}
              >
                {createLabel}
              </span>
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}
